import filesApi from "../api/filesApi";
import { STATIC_ICONS } from "../Constants";
import { getErrorMessage } from "./errorUtils";

export const MAP_ICON_RULES = {
  maxBytes: 512 * 1024,
  minWidth: 24,
  minHeight: 24,
  maxWidth: 512,
  maxHeight: 512,
  maxAspectRatio: 2,
  accept: "image/png,image/jpeg,image/webp,image/svg+xml",
};

const ALLOWED_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
]);

export function getMapIconUrl(icon) {
  const value = String(icon || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:image/")) return value;
  return `${STATIC_ICONS}${value.replace(/^\/+/, "")}`;
}

export function normalizeStoredIconPath(icon) {
  const value = String(icon || "").trim();
  if (!value || /^https?:\/\//i.test(value) || value.startsWith("data:image/")) {
    return "";
  }
  return value.replace(/^\/+/, "").replace(/^icons\//i, "");
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

export async function validateMapIconFile(file) {
  if (!(file instanceof File)) {
    throw new Error("Please choose a valid image file.");
  }

  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    throw new Error("Map icon must be PNG, JPG, WebP, or SVG.");
  }

  if (file.size > MAP_ICON_RULES.maxBytes) {
    throw new Error("Map icon must be 512 KB or smaller.");
  }

  const dimensions = await readImageDimensions(file);
  const width = Number(dimensions.width);
  const height = Number(dimensions.height);

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    throw new Error("Could not read map icon dimensions.");
  }

  if (
    width < MAP_ICON_RULES.minWidth ||
    height < MAP_ICON_RULES.minHeight ||
    width > MAP_ICON_RULES.maxWidth ||
    height > MAP_ICON_RULES.maxHeight
  ) {
    throw new Error("Map icon dimensions must be between 24x24 and 512x512 pixels.");
  }

  const aspectRatio = Math.max(width / height, height / width);
  if (aspectRatio > MAP_ICON_RULES.maxAspectRatio) {
    throw new Error("Map icon width and height must be reasonably balanced.");
  }

  return dimensions;
}

export async function uploadTemplateMapIcon(file, templateName) {
  await validateMapIconFile(file);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", "icons");
  formData.append("purpose", "template-map-icon");
  formData.append("templateName", String(templateName || "template").trim() || "template");

  try {
    const res = await filesApi.upload(formData);
    const fileName = res.data?.data?.fileName;
    if (!fileName) {
      throw new Error("Upload finished, but the server did not return a file name.");
    }
    return fileName;
  } catch (err) {
    throw new Error(getErrorMessage(err, "Could not upload the map icon."));
  }
}

export async function deleteStoredMapIcon(icon) {
  const normalized = normalizeStoredIconPath(icon);
  if (!normalized) return;
  await filesApi.delete([normalized]);
}
